import React, { useEffect, useRef, useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import notaCreditoVentaService, { OrdenDesdeNotaLinea } from '../../services/notaCreditoVentaService';
import ordenDevolucionService from '../../services/ordenDevolucionService';
import ubicacionService, { etiquetaUbicacionSeleccion, Ubicacion } from '../../services/ubicacionService';
import { getApiErrorMessage } from '../../utils/apiError';

type Props = {
  notaId: number | null;
  onClose: () => void;
  onCreated?: () => void;
};

const GenerarOrdenDesdeNotaDialog: React.FC<Props> = ({ notaId, onClose, onCreated }) => {
  const { enqueueSnackbar } = useSnackbar();
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const [lineas, setLineas] = useState<OrdenDesdeNotaLinea[]>([]);
  const [ubicaciones, setUbicaciones] = useState<Ubicacion[]>([]);
  const [nroUbicacion, setNroUbicacion] = useState<number | ''>('');
  const [generando, setGenerando] = useState(false);
  const [cargando, setCargando] = useState(false);

  useEffect(() => {
    if (notaId == null) return;
    let cancelled = false;
    setCargando(true);
    setLineas([]);
    void (async () => {
      try {
        const [preview, lista] = await Promise.all([
          notaCreditoVentaService.getParaOrdenDevolucion(notaId),
          ubicacionService.getUbicacionesParaUsuario(),
        ]);
        if (cancelled) return;
        if (!preview.puedeGenerar) {
          enqueueSnackbar(preview.motivo || 'Esta nota no tiene mercadería pendiente de devolver', { variant: 'info' });
          onCloseRef.current();
          return;
        }
        setLineas(preview.lineas ?? []);
        setUbicaciones(lista);
        const sugerida = preview.nroUbicacionSugerida;
        const inicial = sugerida && lista.some((u) => u.nroUbicacion === sugerida)
          ? sugerida
          : lista[0]?.nroUbicacion;
        setNroUbicacion(inicial ?? '');
      } catch (error) {
        if (!cancelled) {
          enqueueSnackbar(getApiErrorMessage(error, 'No se pudo preparar la orden de devolución'), { variant: 'error' });
          onCloseRef.current();
        }
      } finally {
        if (!cancelled) setCargando(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [notaId, enqueueSnackbar]);

  const confirmar = async () => {
    if (notaId == null || nroUbicacion === '') return;
    const items = lineas
      .filter((l) => l.cantidadSugerida > 0)
      .map((l) => ({
        nroItem: l.nroItem,
        cantidad: l.cantidadSugerida,
        nroFormularioRemito: l.nroFormularioRemito,
        nroItemRemito: l.nroItemRemito,
      }));
    if (items.length === 0) {
      enqueueSnackbar('Indicá una cantidad para devolver', { variant: 'warning' });
      return;
    }
    setGenerando(true);
    try {
      const orden = await ordenDevolucionService.generarDesdeNota(notaId, {
        nroUbicacionOrigen: Number(nroUbicacion),
        items,
      });
      enqueueSnackbar(`Orden de devolución #${orden.nroFormulario} generada`, { variant: 'success' });
      onCreated?.();
      onClose();
    } catch (error) {
      enqueueSnackbar(getApiErrorMessage(error, 'No se pudo generar la orden de devolución'), { variant: 'error' });
    } finally {
      setGenerando(false);
    }
  };

  return (
    <Dialog open={notaId != null} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Generar orden de devolución</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <TextField
            select
            label="Depósito de ingreso"
            value={nroUbicacion}
            onChange={(e) => setNroUbicacion(Number(e.target.value))}
            fullWidth
            disabled={cargando}
          >
            {ubicaciones.map((u) => (
              <MenuItem key={u.nroUbicacion} value={u.nroUbicacion}>
                {etiquetaUbicacionSeleccion(u)}
              </MenuItem>
            ))}
          </TextField>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Artículo</TableCell>
                <TableCell align="right">Libre</TableCell>
                <TableCell>Remito</TableCell>
                <TableCell align="right">Cantidad</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {lineas.map((l) => {
                const candidatos = l.candidatos ?? [];
                const tope = Math.min(
                  l.cantidadLibre,
                  candidatos.find((c) => c.nroFormularioRemito === l.nroFormularioRemito && c.nroItemRemito === l.nroItemRemito)?.pendiente
                    ?? l.cantidadSugerida,
                );
                return (
                  <TableRow key={l.nroItem}>
                    <TableCell>
                      {l.codigoArticulo}
                      <Typography variant="caption" display="block" color="text.secondary">
                        {l.descripcion}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">{l.cantidadLibre}</TableCell>
                    <TableCell>
                      {candidatos.length > 1 ? (
                        <TextField
                          select
                          size="small"
                          value={`${l.nroFormularioRemito ?? ''}-${l.nroItemRemito ?? ''}`}
                          onChange={(e) => {
                            const [remito, item] = e.target.value.split('-').map((n) => Number(n));
                            const cand = candidatos.find((c) => c.nroFormularioRemito === remito && c.nroItemRemito === item);
                            setLineas((prev) => prev.map((row) => row.nroItem === l.nroItem
                              ? {
                                  ...row,
                                  nroFormularioRemito: remito,
                                  nroItemRemito: item,
                                  cantidadSugerida: Math.min(row.cantidadLibre, cand?.pendiente ?? row.cantidadSugerida),
                                }
                              : row));
                          }}
                        >
                          {candidatos.map((c) => (
                            <MenuItem key={`${c.nroFormularioRemito}-${c.nroItemRemito}`} value={`${c.nroFormularioRemito}-${c.nroItemRemito}`}>
                              Remito #{c.nroFormularioRemito} · pend. {c.pendiente}
                            </MenuItem>
                          ))}
                        </TextField>
                      ) : (
                        `#${l.nroFormularioRemito ?? ''}`
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <TextField
                        type="number"
                        size="small"
                        value={l.cantidadSugerida}
                        inputProps={{ min: 0, max: tope, step: 'any' }}
                        onChange={(e) => {
                          const parsed = Number(e.target.value);
                          const cantidad = Number.isFinite(parsed) ? Math.min(Math.max(0, parsed), tope) : 0;
                          setLineas((prev) => prev.map((row) => row.nroItem === l.nroItem
                            ? { ...row, cantidadSugerida: cantidad }
                            : row));
                        }}
                        sx={{ width: 110 }}
                      />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancelar</Button>
        <Button variant="contained" onClick={() => void confirmar()} disabled={generando || cargando || nroUbicacion === ''}>
          {generando ? 'Generando…' : 'Generar orden'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default GenerarOrdenDesdeNotaDialog;
